import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TestimonialCarousel from '../components/TestimonialCarousel';
import TrustBadges from '../components/TrustBadges';
import PortfolioSection from '../components/PortfolioSection';
import { useTranslation } from 'react-i18next';

const { FiCheckCircle, FiUsers, FiDollarSign, FiCamera, FiClock, FiShield, FiTrendingUp, FiPhone, FiMail, FiHome, FiStar, FiCalendar, FiBarChart3, FiTarget, FiAward } = FiIcons;

const Home = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: FiCamera,
      title: t('Marketing Digitale'),
      description: t('Gestione completa presenza online su tutte le piattaforme principali con fotografia professionale e ottimizzazione SEO.'),
      features: [
        t('Multi-piattaforma'),
        t('Fotografia professionale'),
        t('SEO ottimizzato'),
        t('Calendario sincronizzato')
      ]
    },
    {
      icon: FiUsers,
      title: t('Guest Management'),
      description: t('Comunicazione professionale con gli ospiti,check-in automatico e assistenza continua per garantire recensioni eccellenti.'),
      features: [
        t('Comunicazione multilingue'),
        t('Check-in automatico'),
        t('Assistenza H24'),
        t('Gestione emergenze')
      ]
    },
    {
      icon: FiCheckCircle,
      title: t('Facility Management'),
      description: t('Standard professionali di pulizia e manutenzione per mantenere l\'immobile sempre in condizioni ottimali.'),
      features: [
        t('Pulizie certificate'),
        t('Controllo qualità'),
        t('Rifornimento forniture'),
        t('Manutenzione preventiva')
      ]
    },
    {
      icon: FiBarChart3,
      title: t('Revenue Optimization'),
      description: t('Pricing dinamico e analisi di mercato per massimizzare i ricavi attraverso strategie data-driven.'),
      features: [
        t('Pricing dinamico'),
        t('Analisi mercato'),
        t('Report mensili'),
        t('Previsioni stagionali')
      ]
    }
  ];

  const guarantees = [
    {
      icon: FiShield,
      title: t('Garanzia Risultati'),
      description: t('Aumento minimo del 25% dei ricavi o rimborso commissione primo mese')
    },
    {
      icon: FiClock,
      title: t('Attivazione Rapida'),
      description: t('Immobile online e prenotabile entro 7 giorni dalla firma')
    },
    {
      icon: FiCheckCircle,
      title: t('Trasparenza Totale'),
      description: t('Report mensili dettagliati con tutti i dati di performance')
    },
    {
      icon: FiAward,
      title: t('Qualità Certificata'),
      description: t('Standard riconosciuti da Airbnb e Booking.com')
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-warm-50 to-primary-100 py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-white bg-opacity-40">
          <div className="w-full h-full opacity-40" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f17834' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`}} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="flex justify-center lg:justify-start mb-6">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752009564982-photo_5999181830990974370_w.jpg" 
                  alt="Elvira Vigliano Property Manager" 
                  className="h-24 w-24 rounded-full border-4 border-primary-200 shadow-lg" 
                />
              </div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6 }}
                className="mb-4"
              >
                <span className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {t('Property Manager Certificata')}
                </span>
              </motion.div>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-6xl font-bold text-warm-900 mb-6 leading-tight"
              >
                Elvira Vigliano 
                <span className="block text-primary-600 text-2xl md:text-3xl mt-2">
                  {t('Elvira Vigliano Property Management Solutions')}
                </span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl text-warm-700 mb-8 max-w-3xl mx-auto lg:mx-0 leading-relaxed"
              >
                {t('Gestione professionale di affitti brevi con approccio data-driven per massimizzare il ROI del tuo investimento immobiliare')}
              </motion.p>
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.3 }}
                className="grid grid-cols-2 gap-4 mb-8 max-w-lg mx-auto lg:mx-0"
              >
                <div className="flex items-center space-x-2 bg-white/70 backdrop-blur-sm rounded-lg p-3">
                  <SafeIcon icon={FiTrendingUp} className="h-4 w-4 text-primary-600" />
                  <span className="text-warm-800 text-sm">+45% ROI</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/70 backdrop-blur-sm rounded-lg p-3">
                  <SafeIcon icon={FiStar} className="h-4 w-4 text-primary-600" />
                  <span className="text-warm-800 text-sm">4.9/5 Rating</span>
                </div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link 
                  to="/contatti" 
                  className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg"
                >
                  {t('Consulenza Gratuita')}
                </Link>
              </motion.div>
            </div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }} 
              animate={{ opacity: 1, scale: 1 }} 
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-white to-warm-50 p-2">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753287373694-blob" 
                  alt="Villa Monterosso - Cinque Terre" 
                  className="w-full h-80 lg:h-96 object-cover rounded-xl" 
                />
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-warm-900">Villa Monterosso</span>
                  </div>
                  <div className="text-xs text-warm-600 mt-1">+€2.800/mese</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <TrustBadges />

      {/* Form Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-warm-50 rounded-2xl shadow-lg overflow-hidden">
            <div className="p-1 bg-gradient-to-r from-primary-500 to-primary-600">
              <div className="bg-white p-6">
                <h2 className="text-2xl font-bold text-warm-900 mb-2 text-center">
                  {t('Accedi al simulatore gratuito')}
                </h2>
                <p className="text-warm-600 text-center mb-6">
                  {t('Scopri quanto puoi guadagnare dal tuo immobile')}
                </p>
                <iframe 
                  src="https://api.leadconnectorhq.com/widget/form/Q1aA7qjvYqwmVq4jZAFR" 
                  style={{width: '100%', height: '492px', border: 'none'}} 
                  id="inline-Q1aA7qjvYqwmVq4jZAFR" 
                  data-layout="{'id':'INLINE'}" 
                  data-trigger-type="alwaysShow" 
                  data-trigger-value="" 
                  data-activation-type="alwaysActivated" 
                  data-activation-value="" 
                  data-deactivation-type="neverDeactivate" 
                  data-deactivation-value="" 
                  data-form-name="FormInfoBase" 
                  data-height="492" 
                  data-layout-iframe-id="inline-Q1aA7qjvYqwmVq4jZAFR" 
                  data-form-id="Q1aA7qjvYqwmVq4jZAFR" 
                  title="FormInfoBase"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-warm-50" id="servizi">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-warm-900 mb-4"
            >
              {t('Servizi Integrati')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }} 
              viewport={{ once: true }}
              className="text-xl text-warm-600 max-w-2xl mx-auto"
            >
              {t('Soluzione completa per la gestione professionale di affitti brevi')}
            </motion.p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="flex items-start space-x-6">
                  <div className="bg-primary-600 w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={service.icon} className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-warm-900 mb-3">{service.title}</h3>
                    <p className="text-warm-700 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-primary-600" />
                          <span className="text-sm text-warm-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <PortfolioSection />

      {/* Guarantees */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-warm-900 mb-4"
            >
              {t('Garanzie Professionali')}
            </motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {guarantees.map((guarantee, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-primary-600 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={guarantee.icon} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-warm-900 mb-3">{guarantee.title}</h3>
                <p className="text-warm-600 text-sm">{guarantee.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-warm-900 mb-4"
            >
              {t('Testimonianze Clienti')}
            </motion.h2>
          </div>
          <TestimonialCarousel />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t('Inizia la Trasformazione del Tuo Investimento')}
              </h2>
              <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
                {t('Unisciti a oltre 150 proprietari che hanno ottimizzato i loro investimenti immobiliari con la nostra gestione professionale.')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">7 giorni</div>
                  <div className="text-primary-200 text-sm">{t('Setup completo')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">25%+</div>
                  <div className="text-primary-200 text-sm">{t('ROI garantito')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">24/7</div>
                  <div className="text-primary-200 text-sm">{t('Supporto')}</div>
                </div>
              </div>
              <div className="flex justify-center">
                <Link 
                  to="/contatti" 
                  className="inline-flex items-center justify-center bg-white text-primary-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
                >
                  {t('Contattaci')}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;